import {
    InfoOutlined as InfoOutlinedIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import LazyTooltip from 'components/LazyTooltip';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from 'components/ui/select';
import { MultiplierType } from 'pages/invoices/util/InvoicesPageUtil';
import React, { useCallback, useState } from 'react';
import { Control, Controller, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterForm, InvoiceTimecard, InvoiceTimecardForm } from 'types/invoice-details';
import { EditIntentTypeCode } from 'types/invoices';
import { RemunerationCalcType } from 'types/matters';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { secondsToHours } from 'utils/time';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName } from 'utils/usePaymentCurrencyMode';

type EditingField = 'adjustment' | 'discount' | null;

/** A click-to-edit field's label, in the size and grey of the input's own label. */
const CLICK_TO_EDIT_LABEL =
    'text-grey-900/60 flex items-center gap-1.5 pb-2 text-xs leading-[1.4375em] tracking-[0.00938em]';

/** A click-to-edit field's value, drawn as the outlined input it turns into. */
const clickToEditValueClassName = (isDisabled: boolean, isPaymentCurrencyMode?: boolean) =>
    cn(
        'border-grey-900/23 bg-paper hover:border-grey-900/87 min-h-10 w-full cursor-pointer overflow-hidden rounded-[4px] border px-3.5 py-[8.5px] text-base leading-[1.4375em] text-ellipsis whitespace-nowrap',
        isDisabled && 'bg-grey-900/2 pointer-events-none opacity-[0.38]',
        isPaymentCurrencyMode && 'bg-success/8'
    );

const INFO_ICON = 'text-grey-600 h-[1.2rem]';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    control: Control<InvoiceMatterForm>;
    index: number;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    multiplierTypes: MultiplierType[];
    invoiceTimecard: InvoiceTimecard;
    handleUpdateInvoiceTimecards: (idList: number[], updatedTimecards: InvoiceTimecardForm[]) => void;
    buttonsDisabled: boolean;
    isPaymentCurrencyMode?: boolean;
};

export const AdvancedBillingDisplay = React.memo(
    ({
        control,
        index,
        isRowDisabled,
        readonly,
        onSubmit,
        setValue,
        getValues,
        multiplierTypes,
        invoiceTimecard,
        handleUpdateInvoiceTimecards,
        buttonsDisabled: areButtonsDisabled,
        isPaymentCurrencyMode,
    }: Props) => {
        const [selectedMultiplierType, setSelectedmultiplierType] = useState<string>(
            getValues(`invoiceTimecards.${index}.multiplierType`)?.value || ''
        );

        const handleOnVisibilityIconClick = useCallback(() => {
            const isHidden = getValues(`invoiceTimecards.${index}.isHidden`);
            setValue(`invoiceTimecards.${index}.isHidden`, !isHidden);
            onSubmit();
        }, [getValues, index, onSubmit, setValue]);

        const [isNonBillable, isHidden, editIntentTypeCode, adjustmentAmount, discountRate] = useWatch({
            control,
            name: [
                `invoiceTimecards.${index}.isNonBillable`,
                `invoiceTimecards.${index}.isHidden`,
                `invoiceTimecards.${index}.editIntentTypeCode`,
                `invoiceTimecards.${index}.manualAdjustmentAmount`,
                `invoiceTimecards.${index}.discountRate`,
            ],
        });

        const [editingField, setEditingField] = useState<EditingField>(null);

        const handleMultiplierTypeChange = useCallback(
            (value: string | null) => {
                setSelectedmultiplierType(value ?? '');
                const multiplierType = multiplierTypes.find((item) => item.value === value);
                if (multiplierType) {
                    setValue(`invoiceTimecards.${index}.multiplierType`, multiplierType);
                    setValue(`invoiceTimecards.${index}.multiplierRate`, multiplierType.multiplierRate);
                    onSubmit();
                }
            },
            [index, multiplierTypes, onSubmit, setValue]
        );

        const handleDiscountRateSubmit = useCallback(
            (value: string | undefined) => {
                if (!value) {
                    setValue(`invoiceTimecards.${index}.discountRate`, 0);
                }
                onSubmit();
            },
            [setValue, index, onSubmit]
        );

        const onPreserveNonBillableHoursChange = useCallback(
            (checked: boolean) => {
                if (checked) {
                    const billedTime = getValues(`invoiceTimecards.${index}.billedTimeInSeconds`);
                    setValue(`invoiceTimecards.${index}.billedTimeInHours`, String(secondsToHours(billedTime ?? 0, 2)));
                } else {
                    setValue(`invoiceTimecards.${index}.billedTimeInHours`, '0');
                }
                handleUpdateInvoiceTimecards([invoiceTimecard.id], getValues('invoiceTimecards'));
            },
            [handleUpdateInvoiceTimecards, invoiceTimecard.id, getValues, index, setValue]
        );

        const onRemEqualsUsedTimeChange = useCallback(
            (checked: boolean) => {
                if (checked) {
                    setValue(`invoiceTimecards.${index}.remunerationCalcType`, RemunerationCalcType.EQUALS_USED_TIME);
                } else {
                    setValue(`invoiceTimecards.${index}.remunerationCalcType`, RemunerationCalcType.EQUALS_BILLED_TIME);
                }
                handleUpdateInvoiceTimecards([invoiceTimecard.id], getValues('invoiceTimecards'));
            },
            [handleUpdateInvoiceTimecards, invoiceTimecard.id, getValues, index, setValue]
        );

        const isUserPrivilegedToUpdateRemunerationTime = useHasPermissions([
            'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
            'UPDATE.INVOICES.REMUNERATION',
        ]);
        const isRemunerationControlEnabled = useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL);
        const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);

        const hasPermissionToEnableMirroring = useHasPermissions(['UPDATE.INVOICES.TC_EXP_MIRROR']);

        return isRowDisabled && editIntentTypeCode === EditIntentTypeCode.MARK_AS_NONBILLABLE ? (
            <div className="pointer-events-auto">
                {!hideDontNullifyOption ? (
                    <CheckboxInput
                        control={control}
                        name={`invoiceTimecards.${index}.preserveNonBillableHours`}
                        label="timecards.preserve-non-billable-hours"
                        onChange={onPreserveNonBillableHoursChange}
                        disabled={areButtonsDisabled}
                        rightLabelComponent={<InfoTooltip text={'timecards.tooltips.preserve-non-billable-hours'} />}
                    />
                ) : null}
                {isRemunerationControlEnabled && isUserPrivilegedToUpdateRemunerationTime ? (
                    <CheckboxInput
                        control={control}
                        name={`invoiceTimecards.${index}.remEqualsUsedTime`}
                        label="timecards.rem-equals-used-time"
                        onChange={onRemEqualsUsedTimeChange}
                        disabled={areButtonsDisabled}
                    />
                ) : null}
            </div>
        ) : (
            <div className="opacity-decreasing-when-disabled">
                <div className="flex flex-row flex-wrap gap-2">
                    {editingField === 'adjustment' ? (
                        <DecimalInput
                            wrapperClassName={cn(
                                'max-w-[100px]',
                                isPaymentCurrencyMode && paymentCurrencyInputClassName
                            )}
                            control={control}
                            label="invoices.details.table.adjustment"
                            rightLabelComponent={
                                <LazyTooltip title={t('invoices.details.table.add-minus-or-plus-amount')}>
                                    <InfoOutlinedIcon className={INFO_ICON} />
                                </LazyTooltip>
                            }
                            name={`invoiceTimecards.${index}.manualAdjustmentAmount`}
                            allowNegative
                            onBlur={() => {
                                setEditingField(null);
                                onSubmit();
                            }}
                            readonly={readonly}
                            disabled={isNonBillable || areButtonsDisabled}
                            autoFocus
                        />
                    ) : (
                        <div className="w-full max-w-[100px]">
                            <div className={CLICK_TO_EDIT_LABEL}>
                                {t('invoices.details.table.adjustment')}
                                <LazyTooltip title={t('invoices.details.table.add-minus-or-plus-amount')}>
                                    <InfoOutlinedIcon className={INFO_ICON} />
                                </LazyTooltip>
                            </div>
                            <div
                                className={clickToEditValueClassName(
                                    !!isNonBillable || areButtonsDisabled || readonly,
                                    isPaymentCurrencyMode
                                )}
                                role="button"
                                tabIndex={isNonBillable || areButtonsDisabled || readonly ? -1 : 0}
                                onClick={
                                    isNonBillable || areButtonsDisabled || readonly
                                        ? undefined
                                        : () => setEditingField('adjustment')
                                }
                                onKeyDown={(event) => {
                                    if (isNonBillable || areButtonsDisabled || readonly) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setEditingField('adjustment');
                                    }
                                }}
                            >
                                {adjustmentAmount ?? ''}
                            </div>
                        </div>
                    )}
                    {editingField === 'discount' ? (
                        <PercentageInput
                            wrapperClassName={cn(
                                'max-w-[100px]',
                                isPaymentCurrencyMode && paymentCurrencyInputClassName
                            )}
                            control={control}
                            label="invoices.details.table.timecard-discount-pct"
                            name={`invoiceTimecards.${index}.discountRate`}
                            onBlur={(value) => {
                                setEditingField(null);
                                handleDiscountRateSubmit(value);
                            }}
                            readonly={readonly}
                            disabled={isNonBillable || areButtonsDisabled}
                            autoFocus
                        />
                    ) : (
                        <div className="w-full max-w-[100px]">
                            <div className={CLICK_TO_EDIT_LABEL}>
                                {t('invoices.details.table.timecard-discount-pct')}
                            </div>
                            <div
                                className={clickToEditValueClassName(
                                    !!isNonBillable || areButtonsDisabled || readonly,
                                    isPaymentCurrencyMode
                                )}
                                role="button"
                                tabIndex={isNonBillable || areButtonsDisabled || readonly ? -1 : 0}
                                onClick={
                                    isNonBillable || areButtonsDisabled || readonly
                                        ? undefined
                                        : () => setEditingField('discount')
                                }
                                onKeyDown={(event) => {
                                    if (isNonBillable || areButtonsDisabled || readonly) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setEditingField('discount');
                                    }
                                }}
                            >
                                {discountRate ?? ''}
                            </div>
                        </div>
                    )}
                </div>
                <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_TIMECARD_EXTRA_OPTIONS}>
                    <div className="flex flex-row flex-wrap gap-2 py-2">
                        <FeatureFlagged action="show" flag={FeatureFlag.TC_MULTIPLIER}>
                            <Controller
                                control={control}
                                name={`invoiceTimecards.${index}.multiplierType`}
                                render={() => (
                                    <Select
                                        value={selectedMultiplierType || null}
                                        onValueChange={handleMultiplierTypeChange}
                                        disabled={readonly || isRowDisabled || isNonBillable || areButtonsDisabled}
                                    >
                                        <SelectTrigger
                                            size="sm"
                                            className="bg-grey-100 [&_svg]:text-primary rounded-[10px] border-none pr-1 pl-3 text-xs font-semibold shadow-none"
                                        >
                                            <SelectValue>
                                                {(value: string | null) =>
                                                    multiplierTypes.find((type) => type.value === value)?.label ?? ''
                                                }
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectLabel className="text-fg font-semibold">
                                                    {t('invoices.details.table.multiplier')}
                                                </SelectLabel>
                                                {multiplierTypes.map((type) => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </FeatureFlagged>
                        <CheckboxInput
                            control={control}
                            label={'invoices.details.table.mirrored'}
                            name={`invoiceTimecards.${index}.isMirrored`}
                            onChange={onSubmit}
                            disabled={!hasPermissionToEnableMirroring || areButtonsDisabled}
                            readonly={readonly}
                        />
                        <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                            <Controller
                                control={control}
                                name={`invoiceTimecards.${index}.isHidden`}
                                render={({ field }) => (
                                    <LazyTooltip title={t(`invoices.details.table.${isHidden ? 'unhide' : 'hide'}`)}>
                                        <div
                                            className={cn(
                                                'flex cursor-pointer items-center justify-center rounded-md p-1.5',
                                                field.value && !areButtonsDisabled
                                                    ? 'bg-secondary-light'
                                                    : 'bg-grey-100',
                                                (isRowDisabled || readonly || !hasPermissionToEnableMirroring) &&
                                                    'pointer-events-none'
                                            )}
                                            role="button"
                                            tabIndex={areButtonsDisabled ? -1 : 0}
                                            aria-label={t(`invoices.details.table.${isHidden ? 'unhide' : 'hide'}`)}
                                            onClick={areButtonsDisabled ? undefined : handleOnVisibilityIconClick}
                                            onKeyDown={(event) => {
                                                if (areButtonsDisabled) return;
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleOnVisibilityIconClick();
                                                }
                                            }}
                                        >
                                            <span
                                                className="t-icon-swap"
                                                data-state={field.value ? 'b' : 'a'}
                                                aria-hidden="true"
                                            >
                                                <span className="t-icon" data-icon="a">
                                                    <VisibilityIcon fontSize="small" />
                                                </span>
                                                <span className="t-icon" data-icon="b">
                                                    <VisibilityOffIcon fontSize="small" />
                                                </span>
                                            </span>
                                        </div>
                                    </LazyTooltip>
                                )}
                            />
                        </FeatureFlagged>
                    </div>
                </FeatureFlagged>
            </div>
        );
    }
);
